import { NgModule, Injector } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { createCustomElement } from '@angular/elements';

import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { LetModule, PushModule } from '@ngrx/component';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { NgScrollbarModule } from 'ngx-scrollbar';

import { featureKey, reducer, DataEffects, SummaryEffects, ActivityEffects, LastHeroEffects } from 'app/virtual-event/features';

import { CoreModule } from 'app/core/core.module';
import { DialogModule } from 'app/dialog/dialog.module';
import { ToastModule } from 'app/toast/toast.module';
import { VirtualEventRunComponent } from './components/virtual-event/virtual-event.component';
import { CampaignListComponent } from './components/campaign-list/campaign-list.component';
import { PlaylistComponent } from './components/playlist/playlist.component';
import { CurrentlyLiveOptionComponent } from './components/currently-live-option/currently-live-option.component';
import { ActivityListComponent } from './components/activity-list/activity-list.component';

import { CloseSilentAuctionComponent } from './components/modals/close-silent-auction/close-silent-auction.component';
import { DeclareLastHeroComponent } from './components/modals/declare-last-hero/declare-last-hero.component';
import { CurrentlyLiveCampaignComponent } from './components/currently-live-campaign/currently-live-campaign.component';
import { TimeElapsedComponent } from './components/time-elapsed/time-elapsed.component';
import { GoalComponent } from './components/goal/goal.component';
import { UsersComponent } from './components/users/users.component';
import { StatusComponent } from './components/status/status.component';
import { IconModule } from '../icon/icon.module';
import { ActivityComponent } from './components/activity/activity.component';
import { CampaignOptionListComponent } from './components/campaign-option-list/campaign-option-list.component';
import { CurrentlyLiveLastHeroComponent } from './components/currently-live-last-hero/currently-live-last-hero.component';
import { StatusControlComponent } from './components/status-control/status-control.component';
import { UpdateVirtualEventComponent } from './components/modals/update-virtual-event/update-virtual-event.component';

import {LayoutModule} from '@angular/cdk/layout';
import {ScrollingModule} from '@angular/cdk/scrolling';
import { CurrentlyLiveAuctionComponent } from './components/currently-live-auction/currently-live-auction.component';
import { ShowLiveAuctionMultiComponent } from './components/modals/show-live-auction-multi/show-live-auction-multi.component';
import { ChatModule } from '../chat/chat.module';
import { VirtualEventEditorComponent } from './components/virtual-event-editor/virtual-event-editor.component';
import { AngularCropperjsModule } from 'angular-cropperjs';
import { VirtualEventSponsorListComponent } from './components/virtual-event-sponsor-list/virtual-event-sponsor-list.component';
import { VirtualEventSlideListComponent } from './components/virtual-event-slide-list/virtual-event-slide-list.component';
import { DragDropModule } from '@angular/cdk/drag-drop';

import { NgxCurrencyInputMode, provideEnvironmentNgxCurrency, NgxCurrencyDirective } from "ngx-currency";
import { QuillModule } from 'ngx-quill'
import Counter from './counter';
import { RecentActivitiesComponent } from './components/auctioneer-screen/recent-activities/recent-activities.component';
import { RecentActivityComponent } from './components/auctioneer-screen/recent-activity/recent-activity.component';
import { ProgressLineComponent } from './components/auctioneer-screen/progress-line/progress-line.component';
import { CurrentCampaignInfoComponent } from './components/auctioneer-screen/current-campaign-info/current-campaign-info.component';
import { CurrentCampaignOptionComponent } from './components/auctioneer-screen/current-campaign-option/current-campaign-option.component';
import { CurrentAuctionOptionComponent } from './components/auctioneer-screen/current-auction-option/current-auction-option.component';
import { CurrentLastHeroOptionComponent } from './components/auctioneer-screen/current-last-hero-option/current-last-hero-option.component';
import { MdbInfiniteScrollModule } from 'mdb-angular-ui-kit/infinite-scroll';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { MdbAccordionModule } from 'mdb-angular-ui-kit/accordion';
import { MdbCheckboxModule } from 'mdb-angular-ui-kit/checkbox';
import { MdbSelectModule } from 'mdb-angular-ui-kit/select';
import { MdbTabsModule } from 'mdb-angular-ui-kit/tabs';

@NgModule({
  declarations: [
    VirtualEventRunComponent,
    PlaylistComponent,
    CurrentlyLiveOptionComponent,
    ActivityListComponent,
    CampaignListComponent,
    CloseSilentAuctionComponent,
    DeclareLastHeroComponent,
    CurrentlyLiveCampaignComponent,
    TimeElapsedComponent,
    GoalComponent,
    UsersComponent,
    StatusComponent,
    ActivityComponent,
    CampaignOptionListComponent,
    CurrentlyLiveLastHeroComponent,
    StatusControlComponent,
    UpdateVirtualEventComponent,
    CurrentlyLiveAuctionComponent,
    ShowLiveAuctionMultiComponent,
    VirtualEventEditorComponent,
    VirtualEventSponsorListComponent,
    VirtualEventSlideListComponent,
    RecentActivitiesComponent,
    RecentActivityComponent,
    ProgressLineComponent,
    CurrentCampaignInfoComponent,
    CurrentCampaignOptionComponent,
    CurrentAuctionOptionComponent,
    CurrentLastHeroOptionComponent
  ],
  imports: [
    CommonModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    LetModule,
    PushModule,
    StoreModule.forRoot({}, {
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      },
    }),
    StoreModule.forFeature(featureKey, reducer),
    EffectsModule.forRoot([]),
    EffectsModule.forFeature([DataEffects, SummaryEffects, ActivityEffects, LastHeroEffects]),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
    }),
    CoreModule,
    DialogModule,
    ToastModule,
    IconModule,
    LayoutModule,
    ScrollingModule,
    NgScrollbarModule,
    AngularCropperjsModule,
    ChatModule,
    DragDropModule,
    MdbInfiniteScrollModule,
    MdbFormsModule,
    MdbAccordionModule,
    MdbCheckboxModule,
    MdbSelectModule,
    MdbTabsModule,
    QuillModule.forRoot({
      customModules: [{
        implementation: Counter,
        path: 'modules/counter'
      }],
      customOptions: [{
        import: 'formats/font',
        whitelist: ['mirza', 'roboto', 'aref', 'serif', 'sansserif', 'monospace']
      }]
    }),
    NgxCurrencyDirective
  ],
  providers: [provideEnvironmentNgxCurrency({
    align: "left",
    allowNegative: false,
    allowZero: true,
    decimal: ".",
    precision: 0,
    prefix: "$ ",
    suffix: "",
    thousands: ",",
    nullable: true,
    min: null,
    max: null,
    inputMode: NgxCurrencyInputMode.Financial
  })],
  exports: [VirtualEventRunComponent]
})
export class VirtualEventModule {
  constructor(injector: Injector) {
    const VirtualEventElement = createCustomElement(VirtualEventRunComponent, {injector});
    const VirtualEventEditorElement = createCustomElement(VirtualEventEditorComponent, {injector});

    try {
      customElements.define('paybee-virtual-event-element', VirtualEventElement);
      customElements.define('paybee-virtual-event-editor-element', VirtualEventEditorElement);
    } catch(e) {}
  }
}
